import { useEffect, useState, useCallback, useRef } from "react";
import { IResourceData } from "../types/resource";
import {
  getAllResourceList,
  getResourceList,
} from "../services/resourcesService";

interface IuseFetchAllResources {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  currentDomainId?: string | number | null;
  fetchOnMount?: boolean; // Controls whether to fetch data immediately on mount
  isScoreConfigAssociated?: boolean;
}

const useFetchAllResources = ({
  setIsLoading,
  currentDomainId,
  fetchOnMount = true, // Default to true for backward compatibility
  isScoreConfigAssociated,
}: IuseFetchAllResources) => {
  const [resourcesData, setResourcesData] = useState<IResourceData[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false); // Internal loading state for Autocomplete

  // Use refs for guards and mutable props so the callback identity stays stable,
  // preventing useEffect re-triggers from unrelated re-renders.
  const hasFetchedRef = useRef<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);
  const setIsLoadingRef = useRef(setIsLoading);
  setIsLoadingRef.current = setIsLoading;

  const currentDomainIdRef = useRef(currentDomainId);
  currentDomainIdRef.current = currentDomainId;

  const isScoreConfigAssociatedRef = useRef(isScoreConfigAssociated);
  isScoreConfigAssociatedRef.current = isScoreConfigAssociated;

  const fetchResources = useCallback(async () => {
    const domainId = currentDomainIdRef.current;

    // Prevent duplicate fetches AND concurrent in-flight requests
    if (hasFetchedRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);
    setIsLoadingRef.current(true);
    try {
      let result: IResourceData[] = [];
      if (domainId && Number(domainId) > 0) {
        result = await getResourceList({
          currentDomainId: domainId,
          isScoreConfigAssociated: isScoreConfigAssociatedRef.current,
        });
      } else {
        result = await getAllResourceList();
      }
      const reversedResult =
        result && Array.isArray(result) ? result.reverse() : [];
      setResourcesData(reversedResult);
      hasFetchedRef.current = true;
    } catch (err) {
      console.error("Error fetching resources:", err);
      setResourcesData([]);
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsLoadingRef.current(false);
    }
  }, []); // No deps — uses refs for all mutable values

  // Reset hasFetched when currentDomainId changes so data is re-fetched for the new domain
  useEffect(() => {
    hasFetchedRef.current = false;
    if (fetchOnMount) {
      fetchResources();
    }
  }, [fetchOnMount, fetchResources, currentDomainId]);

  return [resourcesData, fetchResources, isFetching] as const;
};

export default useFetchAllResources;
